import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";
import type { DayRecord, Profile } from "../shared/domain";

const NS = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const REL_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const PACKAGE_REL_NS = "http://schemas.openxmlformats.org/package/2006/relationships";
const CONTENT_TYPES_NS = "http://schemas.openxmlformats.org/package/2006/content-types";
const XML_NS = "http://www.w3.org/XML/1998/namespace";
const WORKSHEET_CONTENT_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml";

export interface LeaveRequest {
  startDate: string;
  endDate: string;
  dayCount: number;
  reason: string;
}

function parseXml(bytes: Uint8Array): XMLDocument {
  const document = new DOMParser().parseFromString(strFromU8(bytes), "application/xml");
  if (document.querySelector("parsererror")) throw new Error("休暇届テンプレートを読み込めませんでした。");
  return document;
}

function serializeXml(document: XMLDocument): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(strToU8(new XMLSerializer().serializeToString(document)));
}

function cell(document: XMLDocument, reference: string): Element {
  const found = Array.from(document.getElementsByTagNameNS(NS, "c")).find((node) => node.getAttribute("r") === reference);
  if (!found) throw new Error(`休暇届のセル ${reference} が見つかりません。`);
  return found;
}

function clearCell(target: Element): void {
  while (target.firstChild) target.removeChild(target.firstChild);
  target.removeAttribute("t");
}

function setNumber(document: XMLDocument, reference: string, value: number): void {
  const target = cell(document, reference);
  clearCell(target);
  const node = document.createElementNS(NS, "v");
  node.textContent = String(value);
  target.append(node);
}

function setText(document: XMLDocument, reference: string, value: string): void {
  const target = cell(document, reference);
  clearCell(target);
  target.setAttribute("t", "inlineStr");
  const inline = document.createElementNS(NS, "is");
  const text = document.createElementNS(NS, "t");
  if (value.startsWith(" ") || value.endsWith(" ")) text.setAttributeNS(XML_NS, "xml:space", "preserve");
  text.textContent = value;
  inline.append(text);
  target.append(inline);
}

function serialForDate(date: string): number {
  const [year, month, day] = date.split("-").map(Number);
  return Math.round((Date.UTC(year, month - 1, day) - Date.UTC(1899, 11, 30)) / 86_400_000);
}

function isNextDay(previous: string, next: string): boolean {
  return Date.parse(`${next}T00:00:00Z`) - Date.parse(`${previous}T00:00:00Z`) === 86_400_000;
}

export function groupPaidLeaveRequests(days: DayRecord[]): LeaveRequest[] {
  const paidLeave = days
    .filter((day) => day.status === "paid_leave")
    .slice()
    .sort((left, right) => left.workDate.localeCompare(right.workDate));
  const requests: LeaveRequest[] = [];
  for (const day of paidLeave) {
    const reason = day.scheduleText.trim();
    const previous = requests.at(-1);
    if (previous && previous.reason === reason && isNextDay(previous.endDate, day.workDate)) {
      previous.endDate = day.workDate;
      previous.dayCount += 1;
    } else {
      requests.push({ startDate: day.workDate, endDate: day.workDate, dayCount: 1, reason });
    }
  }
  return requests;
}

function fillSheet(document: XMLDocument, profile: Profile, request: LeaveRequest, applicationDate: string): void {
  setText(document, "A8", profile.leaveRecipient);
  setText(document, "B10", profile.leaveOrganization);
  setText(document, "B11", profile.leaveDepartment);
  setText(document, "B12", profile.displayName);
  setNumber(document, "I5", serialForDate(applicationDate));
  setNumber(document, "D16", serialForDate(request.startDate));
  setNumber(document, "G17", request.dayCount);
  setNumber(document, "D18", serialForDate(request.endDate));
  setText(document, "B20", "有給休暇");
  setText(document, "B24", request.reason);
  setText(document, "B29", "");
}

function workbookSheetName(request: LeaveRequest, index: number): string {
  return `休暇届${index + 1}_${request.startDate.slice(5).replace("-", "")}`;
}

function updatePrintArea(workbook: XMLDocument, sheetName: string, localSheetId: number): void {
  let definedNames = workbook.getElementsByTagNameNS(NS, "definedNames")[0];
  if (!definedNames) {
    definedNames = workbook.createElementNS(NS, "definedNames");
    const workbookRoot = workbook.documentElement;
    const calcProperties = workbook.getElementsByTagNameNS(NS, "calcPr")[0];
    workbookRoot.insertBefore(definedNames, calcProperties ?? null);
  }
  let printArea = Array.from(definedNames.getElementsByTagNameNS(NS, "definedName"))
    .find((node) => node.getAttribute("name") === "_xlnm.Print_Area" && node.getAttribute("localSheetId") === String(localSheetId));
  if (!printArea) {
    printArea = workbook.createElementNS(NS, "definedName");
    printArea.setAttribute("name", "_xlnm.Print_Area");
    printArea.setAttribute("localSheetId", String(localSheetId));
    definedNames.append(printArea);
  }
  printArea.textContent = `'${sheetName.replaceAll("'", "''")}'!$A$1:$J$38`;
}

export async function createLeaveRequestWorkbook(options: {
  profile: Profile;
  days: DayRecord[];
  applicationDate: string;
}): Promise<{ blob: Blob; requests: LeaveRequest[] }> {
  const requests = groupPaidLeaveRequests(options.days);
  if (!requests.length) throw new Error("この月には有給の予定がありません。");
  if (requests.some((request) => !request.reason)) throw new Error("有給日の休暇理由を入力してください。");

  const response = await fetch("/templates/leave-form-template.xlsx", { cache: "no-store" });
  if (!response.ok) throw new Error("休暇届テンプレートを取得できませんでした。");
  const archive = unzipSync(new Uint8Array(await response.arrayBuffer()));
  const workbook = parseXml(archive["xl/workbook.xml"]);
  const relationships = parseXml(archive["xl/_rels/workbook.xml.rels"]);
  const contentTypes = parseXml(archive["[Content_Types].xml"]);
  const sheets = workbook.getElementsByTagNameNS(NS, "sheets")[0];
  const baseSheetNode = workbook.getElementsByTagNameNS(NS, "sheet")[0];
  const baseRelationshipId = baseSheetNode.getAttributeNS(REL_NS, "id");
  const baseRelationship = Array.from(relationships.getElementsByTagNameNS(PACKAGE_REL_NS, "Relationship"))
    .find((node) => node.getAttribute("Id") === baseRelationshipId);
  if (!baseRelationship) throw new Error("休暇届シートの関連情報が見つかりません。");
  const baseTarget = baseRelationship.getAttribute("Target") ?? "worksheets/sheet1.xml";
  const basePath = baseTarget.startsWith("/") ? baseTarget.slice(1) : `xl/${baseTarget}`;
  const baseSheetBytes = archive[basePath];
  const targetPrefix = baseTarget.slice(0, baseTarget.lastIndexOf("/") + 1);

  requests.forEach((request, index) => {
    const name = workbookSheetName(request, index);
    const sheet = parseXml(baseSheetBytes);
    fillSheet(sheet, options.profile, request, options.applicationDate);
    if (index === 0) {
      baseSheetNode.setAttribute("name", name);
      archive[basePath] = serializeXml(sheet);
    } else {
      const relationshipId = `rIdLeave${index + 1}`;
      const target = `${targetPrefix}sheet-leave-${index + 1}.xml`;
      const path = target.startsWith("/") ? target.slice(1) : `xl/${target}`;
      const sheetNode = baseSheetNode.cloneNode(false) as Element;
      sheetNode.setAttribute("name", name);
      sheetNode.setAttribute("sheetId", String(index + 1));
      sheetNode.setAttributeNS(REL_NS, "r:id", relationshipId);
      sheets.append(sheetNode);
      const relationship = relationships.createElementNS(PACKAGE_REL_NS, "Relationship");
      relationship.setAttribute("Id", relationshipId);
      relationship.setAttribute("Type", "http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet");
      relationship.setAttribute("Target", target);
      relationships.documentElement.append(relationship);
      const override = contentTypes.createElementNS(CONTENT_TYPES_NS, "Override");
      override.setAttribute("PartName", `/${path}`);
      override.setAttribute("ContentType", WORKSHEET_CONTENT_TYPE);
      contentTypes.documentElement.append(override);
      archive[path] = serializeXml(sheet);
    }
    updatePrintArea(workbook, name, index);
  });

  archive["xl/workbook.xml"] = serializeXml(workbook);
  archive["xl/_rels/workbook.xml.rels"] = serializeXml(relationships);
  archive["[Content_Types].xml"] = serializeXml(contentTypes);
  const corePath = "docProps/core.xml";
  if (archive[corePath]) {
    const core = parseXml(archive[corePath]);
    const modified = Array.from(core.getElementsByTagNameNS("http://purl.org/dc/terms/", "modified"))[0];
    if (modified) modified.textContent = new Date().toISOString();
    archive[corePath] = serializeXml(core);
  }
  return {
    blob: new Blob([zipSync(archive, { level: 6 })], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    requests,
  };
}
